import { Box, Button, Dialog, DialogActions, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import axios from "axios";
import { AuthState } from "context/AuthContext";
import { PimDashboardState } from "context/PimDashboardContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import SearchInputFieldWithDebounce from "components/PimDashboard/CommonComponents/SearchInputFieldWithDebounce/SearchInputFieldWithDebounce";
import { arrType, inputTypes, selectOptionsKeyName, imageTableColumn, styleImageColumn, imageColumn } from "data/globalData";
import {
	pimTrimBreakdownAssociationObj as trimBreakdownAssociationKey,
	trimBreakdownTableColumns as trimBreakdownColumns,
	reqColorLevelColsForTrimBreakdown,
	breakdownGridRowIdKey,
	breakdownRowUpdatedKey,
	normalizeTrimBreakdownRowsForGrid,
	isTruthyBreakdownLevel,
	getTrimItemBreakdownRows,
} from "../../data";
import { trimsTableColumns } from "../../data";
import { trimsMasterColumnsObj } from "components/PimDashboard/DashboardComponents/Sourcing/TrimsMaster/data";
import BreakdownDataGrid from "../../BreakdownDataGrid";

const colStyle = {
	headerAlign: "center",
	align: "center",
};

const trimCodeSearchColumn = {
	name: "Trim Code",
	key: trimBreakdownColumns.trim_code.key,
	input_type: inputTypes.SELECT.key,
	[selectOptionsKeyName]: {
		type: arrType,
		list: [],
		getOptionLabel: (item) => ({
			...item,
			key: item?.trim_code ?? item?.id ?? "",
			name: item?.trim_code ? `${item.trim_code}${item?.item_description ? ` - ${item.item_description}` : ""}` : "",
		}),
	},
};

const TrimBreakdown = ({ open, onClose, selectedTrim, onUpdateTrim, isEditMode = false }) => {
	const { allAttributes } = PimDashboardState();
	const { user } = AuthState();
	const [breakdownRows, setBreakdownRows] = useState([]);
	const trimColorLevelKey = trimsTableColumns.color_level.key;
	const trimSizeLevelKey = trimsTableColumns.size_level.key;
	const trimChannelLevelKey = trimsTableColumns.channel_level.key;

	const getBreakdownRowKey = (row) => row?.[breakdownGridRowIdKey];

	const syncBreakdownRows = (rows) => normalizeTrimBreakdownRowsForGrid(rows, { prefix: "trim-bd" });

	const trimCategoryKey = trimsTableColumns.item_category.key;
	const trimSubCategoryKey = trimsTableColumns.item_sub_category.key;

	const trimMasterFetchCacheKey = useMemo(
		() =>
			[
				selectedTrim?.id ?? "trim",
				selectedTrim?.[trimCategoryKey] ?? selectedTrim?.[trimBreakdownColumns.item_category.key] ?? "",
				selectedTrim?.[trimSubCategoryKey] ?? selectedTrim?.[trimBreakdownColumns.item_sub_category.key] ?? "",
			].join("|"),
		[selectedTrim, trimCategoryKey, trimSubCategoryKey],
	);

	const fetchTrimMasterFromSearch = useCallback(
		async (offset = 0, limit = 20, searchTerm = "") => {
			const itemCategory = selectedTrim?.[trimCategoryKey] ?? selectedTrim?.[trimBreakdownColumns.item_category.key];
			const itemSubCategory =
				selectedTrim?.[trimSubCategoryKey] ?? selectedTrim?.[trimBreakdownColumns.item_sub_category.key];
			const filter = {
				...(itemCategory ? { item_category: itemCategory } : {}),
				...(itemSubCategory ? { item_sub_category: itemSubCategory } : {}),
				...(searchTerm ? { trim_code: searchTerm } : {}),
			};

			const res = await axios.post("/api/sourcing/trims_master/get", {
				limit,
				offset,
				filter,
			});
			return { ...res, data: { ...res.data, result: res?.data?.result?.rows ?? [] } };
		},
		[selectedTrim, trimCategoryKey, trimSubCategoryKey],
	);

	const updateBreakdownRow = (updatedRow) => {
		setBreakdownRows((prev) => {
			const updatedKey = getBreakdownRowKey(updatedRow);
			const nextRows = syncBreakdownRows(
				prev.map((row) =>
					getBreakdownRowKey(row) === updatedKey
						? {
								...updatedRow,
								...(updatedRow?.id ? { [breakdownRowUpdatedKey]: true } : {}),
								last_updated_by: user?.email,
							}
						: row,
				),
			);
			onUpdateTrim?.({ ...selectedTrim, [trimBreakdownAssociationKey]: nextRows });
			return nextRows;
		});
		return updatedRow;
	};

	const handleTrimCodeSelect = ({ row, value }) => {
		const selectedMasterTrim = value ?? {};
		updateBreakdownRow({
			...row,
			[trimBreakdownColumns.trim_code.key]: selectedMasterTrim?.trim_code ?? "",
			[trimsMasterColumnsObj.item_description.key]: selectedMasterTrim?.item_description ?? "",
			[imageColumn.key]: selectedMasterTrim?.url ?? "",
		});
	};

	const breakdownColumns = useMemo(() => {
		const baseColumns = [
			{
				...trimBreakdownColumns.item_category,
				flex: 1.1,
				minWidth: 110,
				...colStyle,
				headerName: trimBreakdownColumns.item_category.name,
				field: trimBreakdownColumns.item_category.key,
			},
			{
				...trimBreakdownColumns.item_sub_category,
				flex: 1.4,
				minWidth: 130,
				...colStyle,
				headerName: trimBreakdownColumns.item_sub_category.name,
				field: trimBreakdownColumns.item_sub_category.key,
			},
		];

		const colorLevelColumns =
			isTruthyBreakdownLevel(selectedTrim?.[trimColorLevelKey])
				? allAttributes
						?.filter((row) => reqColorLevelColsForTrimBreakdown.includes(row?.key))
						.map((row) => ({
							...row,
							flex: 0.9,
							minWidth: 110,
							...colStyle,
							headerName: row.name,
							field: row.key,
						}))
				: [];

		const sizeLevelColumns =
			isTruthyBreakdownLevel(selectedTrim?.[trimSizeLevelKey])
				? [
						{
							...trimBreakdownColumns.size,
							flex: 0.8,
							minWidth: 110,
							...colStyle,
							headerName: trimBreakdownColumns.size.name,
							field: trimBreakdownColumns.size.key,
						},
					]
				: [];

		const channelLevelColumns =
			isTruthyBreakdownLevel(selectedTrim?.[trimChannelLevelKey])
				? [
						{
							...trimBreakdownColumns.channel,
							flex: 0.9,
							minWidth: 110,
							...colStyle,
							headerName: trimBreakdownColumns.channel.name,
							field: trimBreakdownColumns.channel.key,
						},
					]
				: [];

		const detailColumns = [
			{
				...imageTableColumn(styleImageColumn),
				flex: 0.8,
				minWidth: 80,
			},
			{
				...trimsMasterColumnsObj.trim_code,
				flex: 0.9,
				minWidth: 200,
				...colStyle,
				headerName: trimsMasterColumnsObj.trim_code.name,
				field: trimsMasterColumnsObj.trim_code.key,
				renderCell: ({ row }) => (
					<Box width="100%" height="100%" display="flex" alignItems="center" px={1} boxSizing="border-box">
						<SearchInputFieldWithDebounce
							key={`${trimMasterFetchCacheKey}-${getBreakdownRowKey(row)}`}
							column={trimCodeSearchColumn}
							data={{ [trimBreakdownColumns.trim_code.key]: row?.[trimBreakdownColumns.trim_code.key] ?? "" }}
							setData={() => {}}
							fetchData={fetchTrimMasterFromSearch}
							cacheKey={`trim-master-${trimMasterFetchCacheKey}`}
							selectOptKeyName={selectOptionsKeyName}
							disableLabel
							storeValueAs="object"
							width="100%"
							disabled={!isEditMode}
							onChange={(event) => handleTrimCodeSelect({ row, value: event?.target?.value })}
							inputSx={{
								"& .MuiStack-root": { height: "100%" },
								"& .MuiAutocomplete-root": { height: "100%" },
								"& .MuiAutocomplete-inputRoot": { height: "100%", minHeight: 0 },
								"& .MuiInputBase-root": { height: "100%" },
							}}
						/>
					</Box>
				),
			},
			{
				...trimsMasterColumnsObj.item_description,
				flex: 1.2,
				minWidth: 180,
				...colStyle,
				headerName: trimsMasterColumnsObj.item_description.name,
				field: trimsMasterColumnsObj.item_description.key,
			},
		];

		return [...baseColumns, ...colorLevelColumns, ...sizeLevelColumns, ...channelLevelColumns, ...detailColumns];
	}, [allAttributes, selectedTrim, trimColorLevelKey, trimSizeLevelKey, trimChannelLevelKey, breakdownRows, isEditMode, fetchTrimMasterFromSearch, trimMasterFetchCacheKey]);

	useEffect(() => {
		const existingRows = getTrimItemBreakdownRows(selectedTrim);
		setBreakdownRows(syncBreakdownRows(existingRows?.length ? existingRows : []));
	}, [selectedTrim]);

	const subtitle = [
		selectedTrim?.[trimBreakdownColumns.item_category.key] || "Accessory",
		selectedTrim?.[trimBreakdownColumns.item_sub_category.key]
			? ` - ${selectedTrim[trimBreakdownColumns.item_sub_category.key]}`
			: "",
	]
		.filter(Boolean)
		.join("");

	return (
		<Dialog
			onClose={onClose}
			open={open}
			fullWidth
			maxWidth="lg"
			PaperProps={{
				sx: {
					borderRadius: 3,
					overflow: "hidden",
					border: `1px solid ${grey[300]}`,
					backgroundColor: "#fff",
				},
			}}>
			<Box
				sx={{
					background: `linear-gradient(135deg, ${grey[50]} 0%, #ffffff 55%, ${grey[50]} 100%)`,
					borderBottom: `1px solid ${grey[300]}`,
					px: 3,
					py: 2,
					boxSizing: "border-box",
				}}>
				<Stack spacing={1}>
					<Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
						<Box>
							<Typography variant="h6" sx={{ fontWeight: 800, color: grey[900], lineHeight: 1.15 }}>
								Breakdown
							</Typography>
							<Typography variant="body2" sx={{ color: grey[600], mt: 0.4 }}>
								{subtitle}
							</Typography>
						</Box>
					</Box>
				</Stack>
			</Box>

			<Box width="100%" p={2} sx={{ pt: 1.5, overflow: "hidden", boxSizing: "border-box" }}>
				<Box
					sx={{
						border: `1px solid ${grey[300]}`,
						borderRadius: 2,
						overflow: "hidden",
						backgroundColor: "#fff",
						boxSizing: "border-box",
					}}>
					<BreakdownDataGrid columns={breakdownColumns} rows={breakdownRows} />				</Box>
			</Box>

			<DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
				<Button variant="outlined" onClick={onClose} sx={{ textTransform: "none", fontWeight: 700 }}>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default TrimBreakdown;
